import { useMemo, useState, useEffect, Fragment, useRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import FindReplaceIcon from '@mui/icons-material/FindReplaceOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import Button from '@mui/material/Button';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { DONE_STATUSES } from '../lib/status';
import { findDuplicates, monthsAgo } from '../lib/duplicate-detect';
import { exportDuplicatesXlsx } from '../lib/duplicate-export';
import { fmtInt } from '../lib/format';
import { StatusChip } from './StatusChip';

const THRESHOLD_OPTIONS = [60, 70, 80, 90] as const;
const WINDOW_OPTIONS: Array<{ value: number | 'all'; label: string }> = [
  { value: 3,     label: '≤ 3 Mo' },
  { value: 6,     label: '≤ 6 Mo' },
  { value: 9,     label: '≤ 9 Mo' },
  { value: 12,    label: '≤ 12 Mo' },
  { value: 'all', label: 'Alle' },
];
const JIRA_URL_KEY = 'v2.jira.baseUrl';

// Issue-Key-Format: PROJECT-NUMBER (z.B. CAL-12345 → 'CAL', CALEXT-5904 → 'CALEXT').
function projectPrefix(key: string | undefined): string {
  if (!key) return '';
  const m = key.match(/^([A-Z]+)-/);
  return m ? m[1] : '';
}

// Cutoff-Datum als „TT.MM.JJJJ" — explizite Anzeige der Rolling-Window-Grenze.
function fmtCutoff(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
}

export function DuplicateTickets() {
  const rows = useData(s => s.rows);
  const teamFilter = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);

  const [threshold, setThreshold] = useState<number>(70);
  const [doneWindow, setDoneWindow] = useState<number | 'all'>(6);
  const [jiraBaseUrl, setJiraBaseUrl] = useState<string>(() =>
    (typeof window !== 'undefined' && localStorage.getItem(JIRA_URL_KEY)) || 'https://schueco.atlassian.net'
  );

  useEffect(() => {
    try { localStorage.setItem(JIRA_URL_KEY, jiraBaseUrl.trim()); } catch { /* noop */ }
  }, [jiraBaseUrl]);

  // cutoffMs nur relevant, wenn doneWindow eine Zahl ist; bei 'all' ignoriert
  // die lib das (bScope='all').
  const cutoffMs = useMemo(
    () => typeof doneWindow === 'number' ? monthsAgo(doneWindow) : 0,
    [doneWindow]
  );

  // Suche immer über ALLE Teams — Team-Filter wirkt nur auf die Anzeige.
  // So können wir pro Team einen Counter zeigen, ohne neu suchen zu müssen.
  const result = useMemo(() => {
    if (!rows.length) {
      return { pairs: [], affectedOpenKeys: new Set<string>(), totalOpenItems: 0 };
    }
    return findDuplicates(rows, {
      threshold: threshold / 100,
      minSharedTokens: 3,
      bScope: doneWindow === 'all' ? 'all' : 'open+recent-done',
      recentDoneCutoffMs: cutoffMs,
      doneStatuses: DONE_STATUSES,
      preFilter: 'team+type',
    });
  }, [rows, threshold, cutoffMs, doneWindow]);

  // Pro Team aggregieren — visible (echte Duplikate) und sync (gewollte
  // CAL↔CALEXT-Sync) getrennt. Pairs sind via team+type-Pre-Filter immer
  // same-team, deshalb genügt p.a['Team Name'] als Team-Schlüssel.
  type TeamStat = {
    team: string;
    affected: Set<string>;
    syncAffected: Set<string>;
    visiblePairs: typeof result.pairs;
    syncPairs: typeof result.pairs;
  };
  const { teamStats, openItemsByTeam } = useMemo(() => {
    const open: Map<string, number> = new Map();
    for (const r of rows) {
      const t = r['Issue Type'];
      if (t !== 'Story' && t !== 'Bug') continue;
      const status = (r['Status'] ?? '').trim().toLowerCase();
      if (DONE_STATUSES.has(status)) continue;
      const team = r['Team Name'] ?? '–';
      open.set(team, (open.get(team) ?? 0) + 1);
    }

    const stats = new Map<string, TeamStat>();
    const ensure = (team: string): TeamStat => {
      let s = stats.get(team);
      if (!s) {
        s = { team, affected: new Set(), syncAffected: new Set(), visiblePairs: [], syncPairs: [] };
        stats.set(team, s);
      }
      return s;
    };
    // Sicherstellen, dass auch Teams ohne Matches in der Bar auftauchen
    for (const team of open.keys()) ensure(team);

    for (const p of result.pairs) {
      const team = p.a['Team Name'] ?? '–';
      const s = ensure(team);
      const isSync = p.score >= 0.9999
        && projectPrefix(p.a['Issue key']) !== projectPrefix(p.b['Issue key'])
        && projectPrefix(p.a['Issue key']) !== ''
        && projectPrefix(p.b['Issue key']) !== '';
      if (isSync) {
        s.syncPairs.push(p);
        s.syncAffected.add(p.a['Issue key'] ?? '');
        if (!DONE_STATUSES.has((p.b['Status'] ?? '').toLowerCase().trim())) {
          s.syncAffected.add(p.b['Issue key'] ?? '');
        }
      } else {
        s.visiblePairs.push(p);
        s.affected.add(p.a['Issue key'] ?? '');
        if (!DONE_STATUSES.has((p.b['Status'] ?? '').toLowerCase().trim())) {
          s.affected.add(p.b['Issue key'] ?? '');
        }
      }
    }
    return { teamStats: stats, openItemsByTeam: open };
  }, [result, rows]);

  // Aggregat-Werte: wenn Team-Filter aktiv → nur dieses Team; sonst Summe über alle Teams.
  const { pairs, syncPairs, affectedOpenKeys, syncAffectedOpenKeys, totalOpenItems } = useMemo(() => {
    if (teamFilter) {
      const s = teamStats.get(teamFilter);
      return {
        pairs: s?.visiblePairs ?? [],
        syncPairs: s?.syncPairs ?? [],
        affectedOpenKeys: s?.affected ?? new Set<string>(),
        syncAffectedOpenKeys: s?.syncAffected ?? new Set<string>(),
        totalOpenItems: openItemsByTeam.get(teamFilter) ?? 0,
      };
    }
    const allVis: typeof result.pairs = [];
    const allSync: typeof result.pairs = [];
    const aff = new Set<string>();
    const syncAff = new Set<string>();
    let total = 0;
    for (const s of teamStats.values()) {
      allVis.push(...s.visiblePairs);
      allSync.push(...s.syncPairs);
      s.affected.forEach(k => aff.add(k));
      s.syncAffected.forEach(k => syncAff.add(k));
    }
    for (const n of openItemsByTeam.values()) total += n;
    // Sortier-Ordnung: Score DESC (war in result.pairs schon so, aber durch
    // Concat über Teams nicht garantiert). Re-sortieren.
    allVis.sort((a, b) => b.score - a.score || b.sharedCount - a.sharedCount);
    return {
      pairs: allVis,
      syncPairs: allSync,
      affectedOpenKeys: aff,
      syncAffectedOpenKeys: syncAff,
      totalOpenItems: total,
    };
  }, [teamFilter, teamStats, openItemsByTeam, result.pairs]);

  // Team-Bar-Reihenfolge: nach Affected-Count DESC, dann alphabetisch.
  const teamBar = useMemo(() => {
    const list = [...teamStats.values()].map(s => ({
      team: s.team,
      affected: s.affected.size,
      open: openItemsByTeam.get(s.team) ?? 0,
    }));
    list.sort((a, b) => b.affected - a.affected || a.team.localeCompare(b.team));
    return list;
  }, [teamStats, openItemsByTeam]);

  const totalAcrossTeams = useMemo(
    () => teamBar.reduce((s, t) => s + t.affected, 0),
    [teamBar]
  );
  const totalOpenAcrossTeams = useMemo(
    () => [...openItemsByTeam.values()].reduce((s, n) => s + n, 0),
    [openItemsByTeam]
  );

  const affectedCount = affectedOpenKeys.size;
  const pctAffected = totalOpenItems > 0 ? (affectedCount / totalOpenItems) * 100 : 0;

  // Hovered-Pair-Key für Inline-Vergleichs-Expansion. Tooltip war
  // problematisch bei großen Inhalten (Viewport-Overflow). Stattdessen:
  // beim Hover öffnet sich eine zusätzliche Tabellen-Zeile direkt unter
  // der gehoverten — immer korrekt positioniert.
  const [hoveredPairKey, setHoveredPairKey] = useState<string | null>(null);
  const leaveTimer = useRef<number | null>(null);
  const onRowEnter = (key: string) => {
    if (leaveTimer.current) { window.clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setHoveredPairKey(key);
  };
  const onRowLeave = () => {
    // Kurzes Delay, damit Maus-Bewegung in die expandierte Zeile nicht
    // sofort schließt; man kann den Vergleich also „untersuchen", ohne
    // dass er beim Bewegen weg-flackert.
    leaveTimer.current = window.setTimeout(() => setHoveredPairKey(null), 200);
  };
  useEffect(() => () => { if (leaveTimer.current) window.clearTimeout(leaveTimer.current); }, []);

  const baseUrl = jiraBaseUrl.trim().replace(/\/+$/, '');
  const openIssue = (key: string) => {
    if (!baseUrl || !key) return;
    window.open(`${baseUrl}/browse/${key}`, '_blank', 'noopener,noreferrer');
  };
  // Browser-Popup-Blocker verhindern zwei window.open() in einer User-Geste.
  // Stattdessen: JQL-Suche öffnen, die BEIDE Keys filtert — JIRA zeigt
  // die Treffer direkt als Liste in einem Tab.
  const openBothInJira = (keyA: string, keyB: string) => {
    if (!baseUrl || !keyA || !keyB) return;
    const jql = `key in (${keyA}, ${keyB})`;
    window.open(`${baseUrl}/issues/?jql=${encodeURIComponent(jql)}`, '_blank', 'noopener,noreferrer');
  };

  // Counter-Farbe abhängig vom Anteil — visuell signalisieren, ob das
  // Aufräumen klein oder groß wird.
  const counterColor = affectedCount === 0
    ? { fg: '#1b5e20', bg: '#e6f4ea', border: '#2e7d32' }
    : pctAffected >= 15
      ? { fg: '#b71c1c', bg: '#fce8e6', border: '#c62828' }
      : pctAffected >= 5
        ? { fg: '#bf6800', bg: '#fff8e1', border: '#f57c00' }
        : { fg: '#0d47a1', bg: '#e3f2fd', border: '#1976d2' };

  return (
    <Box>
      {/* Team-Bar: pro Team Affected-Count, klickbar — setzt den globalen Team-Filter.
          „Alle Teams" zeigt die Gesamtsumme. Reagiert sofort auf Schwellen-Änderung. */}
      <Box
        sx={{
          mb: 1.5,
          p: 1.25,
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', mb: 0.75 }}>
          Duplikat-Counter pro Team — Klick wirkt global (alle Karten)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={`Alle Teams · ${fmtInt(totalAcrossTeams)} / ${fmtInt(totalOpenAcrossTeams)}`}
            color={!teamFilter ? 'primary' : 'default'}
            variant={!teamFilter ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: '' })}
            sx={{ fontWeight: !teamFilter ? 700 : 500 }}
          />
          {teamBar.map(t => {
            const isActive = teamFilter === t.team;
            return (
              <Chip
                key={t.team}
                label={`${t.team} · ${fmtInt(t.affected)} / ${fmtInt(t.open)}`}
                color={isActive ? 'primary' : t.affected === 0 ? 'success' : t.affected >= t.open * 0.15 ? 'warning' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => setFilters({ team: t.team })}
                sx={{ fontWeight: isActive ? 700 : 500 }}
              />
            );
          })}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Format: <b>betroffene offene Items</b> / <b>offene Items im Team gesamt</b>. CAL↔CALEXT-Sync ausgenommen.
        </Typography>
      </Box>

      {/* Top-Counter — prominent, reagiert auf Team-Filter und Schwelle */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: 2,
          borderColor: counterColor.border,
          bgcolor: counterColor.bg,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Typography variant="overline" sx={{ color: counterColor.fg, fontWeight: 700, lineHeight: 1, display: 'block' }}>
              Offene Items mit potenziellem Duplikat
            </Typography>
            <Typography sx={{ color: counterColor.fg, fontWeight: 800, fontSize: 48, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(affectedCount)}
            </Typography>
            <Typography variant="caption" sx={{ color: counterColor.fg, fontWeight: 600 }}>
              von {fmtInt(totalOpenItems)} offenen Stories/Bugs
              {totalOpenItems > 0 ? ` (${Math.round(pctAffected)} %)` : ''}
              {teamFilter ? ` · Team „${teamFilter}"` : ' · alle Teams'}
              {` · Schwelle ≥ ${threshold} %`}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.5 }}>
            <Chip
              size="small"
              label={`${fmtInt(pairs.length)} Paar(e)`}
              sx={{ bgcolor: 'background.paper', borderColor: counterColor.border, color: counterColor.fg, fontWeight: 700, border: 1 }}
            />
            <Typography variant="caption" sx={{ color: counterColor.fg, fontStyle: 'italic' }}>
              Match-Suche: offen ↔ offen oder done&nbsp;(≤ 6 Monate)
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Sync-Hinweis: CAL↔CALEXT-100%-Paare sind gewollte Sync-Schnittstelle, nicht in Tabelle */}
      {syncPairs.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
              CAL ↔ CALEXT Sync
            </Typography>
            <Chip size="small" variant="outlined" label={`${fmtInt(syncPairs.length)} 100%-Paar(e) ausgeblendet`} />
            <Chip size="small" variant="outlined" label={`${fmtInt(syncAffectedOpenKeys.size)} offene Items betroffen`} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
              100%-identische Summaries zwischen unterschiedlichen JIRA-Projekten (CAL/CALEXT) gelten als
              gewollte Sync-Schnittstelle und werden weder in der Tabelle gezeigt noch im großen Counter
              gezählt. 100%-Paare innerhalb desselben Projekts bleiben sichtbar — das sind echte Duplikate.
            </Typography>
          </Stack>
        </Box>
      )}

      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <FindReplaceIcon sx={{ color: 'primary.main', mt: 0.5 }} />
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Mögliche Duplikate
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Lexikalische Ähnlichkeit über normalisierte Summary-Tokens (Jaccard).
            Pre-Filter: gleiches Team + Issue Type. Keine semantische Ähnlichkeit — Paraphrasen werden NICHT erkannt, dafür auch keine externe API.
          </Typography>
        </Box>
        <Stack direction="column" spacing={0.5} alignItems="flex-end">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Schwelle:</Typography>
            <ToggleButtonGroup size="small" value={threshold} exclusive onChange={(_, v) => v != null && setThreshold(v)}>
              {THRESHOLD_OPTIONS.map(v => (
                <ToggleButton key={v} value={v}>{`≥ ${v} %`}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Done-Fenster:</Typography>
            <ToggleButtonGroup size="small" value={doneWindow} exclusive onChange={(_, v) => v != null && setDoneWindow(v)}>
              {WINDOW_OPTIONS.map(o => (
                <ToggleButton key={String(o.value)} value={o.value}>{o.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            disabled={pairs.length === 0}
            onClick={() => exportDuplicatesXlsx(pairs, {
              jiraBaseUrl: baseUrl,
              team: teamFilter,
              threshold,
              doneWindow,
            })}
            sx={{ mt: 0.25 }}
          >
            Excel-Export ({fmtInt(pairs.length)})
          </Button>
        </Stack>
      </Stack>

      {/* Klare Status-Definition pro Seite — was wird aus JIRA gezogen? */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', mb: 1 }}>
          Welche JIRA-Status werden auf beiden Seiten berücksichtigt?
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip size="small" color="primary" label="A" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Offenes Item — alle nicht-Done-Status
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {['New','Open','Awaiting Refinement','Backlog','Ready','In Progress','Codereview','In Integration','Awaiting Test','In Test'].map(s => (
                <Chip key={s} size="small" variant="outlined" label={s} sx={{ height: 20, fontSize: 10.5 }} />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: 10.5 }}>
              Jedes offene Item ist Kandidat A — wir suchen für jedes davon mögliche Duplikate.
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip size="small" label="B" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Duplikat-Kandidat — offen ODER abgeschlossen (Done-Fenster {doneWindow === 'all' ? 'Alle' : `≤ ${doneWindow} Monate`})
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10.5, mr: 0.5 }}>offen (kein Datum-Limit):</Typography>
              {['New','Open','Awaiting Refinement','Backlog','Ready','In Progress','Codereview','In Integration','Awaiting Test','In Test'].map(s => (
                <Chip key={`b-open-${s}`} size="small" variant="outlined" label={s} sx={{ height: 20, fontSize: 10.5 }} />
              ))}
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10.5, mr: 0.5 }}>
                {doneWindow === 'all'
                  ? 'oder abgeschlossen (egal wann resolved):'
                  : `oder abgeschlossen mit Resolved-Datum ≥ ${fmtCutoff(cutoffMs)}:`}
              </Typography>
              {['Closed','Resolved','Done','Aborted'].map(s => (
                <Chip key={`b-done-${s}`} size="small" variant="outlined" color="default" label={s} sx={{ height: 20, fontSize: 10.5, opacity: 0.85 }} />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: 10.5 }}>
              {doneWindow === 'all' ? (
                <>
                  <b>Done-Fenster: Alle</b> — sämtliche abgeschlossenen Stories/Bugs werden als Vergleichs-
                  Kandidat zugelassen, unabhängig vom Resolved-Datum. Items ohne Resolved-Datum trotz
                  Done-Status werden auch berücksichtigt. Mehr Treffer, dafür auch Rauschen aus uralten Items.
                </>
              ) : (
                <>
                  <b>Rolling-Window: ≤ {doneWindow} Monate</b> — das Resolved-Datum (JIRA-Feld) muss
                  innerhalb der letzten {doneWindow} Monate liegen (heute − {Math.round(doneWindow * 30.4)} Tage
                  = <b>{fmtCutoff(cutoffMs)}</b>). Ältere Closed-Items oder Items ohne Resolved-Datum
                  fallen raus. Zweck: Regressionen zu kürzlich gefixten Bugs finden, ohne dass uralte
                  Treffer die Liste fluten.
                </>
              )}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="JIRA-Basis-URL"
          value={jiraBaseUrl}
          onChange={e => setJiraBaseUrl(e.target.value)}
          placeholder="https://schueco.atlassian.net"
          sx={{ minWidth: 340, flex: 1 }}
          helperText={`Wird für die „Öffnen“-Buttons in jeder Zeile genutzt. Persistent im Browser gespeichert.`}
        />
        {teamFilter && <Chip size="small" color="primary" label={`Team: ${teamFilter}`} />}
      </Stack>

      {pairs.length === 0 ? (
        <Box sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'action.hover', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Keine Duplikat-Paare ≥ {threshold}% gefunden{teamFilter ? ` für Team „${teamFilter}"` : ''}.
            {' '}Schwelle reduzieren, falls erwartet wurde, dass mehr Treffer kommen.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }} align="right">Score</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Team · Typ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Offenes Item (A)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duplikat-Kandidat (B)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Gemeinsame Tokens</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pairs.slice(0, 500).map(p => {
                const pairKey = `${p.a['Issue key']}__${p.b['Issue key']}`;
                const expanded = hoveredPairKey === pairKey;
                return (
                <Fragment key={pairKey}>
                <TableRow
                  hover
                  onMouseEnter={() => onRowEnter(pairKey)}
                  onMouseLeave={onRowLeave}
                  sx={{ cursor: 'default', ...(expanded ? { bgcolor: 'action.hover' } : {}) }}
                >
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    <ScoreChip score={p.score} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5 }}>
                    <Box>{p.a['Team Name'] ?? '–'}</Box>
                    <Box sx={{ color: 'text.secondary' }}>{p.a['Issue Type']}</Box>
                  </TableCell>
                  <IssueCell issue={p.a} onOpen={openIssue} />
                  <IssueCell issue={p.b} onOpen={openIssue} />
                  <TableCell sx={{ fontSize: 11, maxWidth: 220 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {p.shared.map(t => (
                        <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
                      ))}
                      {p.sharedCount > p.shared.length && (
                        <Typography variant="caption" color="text.secondary">+{p.sharedCount - p.shared.length}</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip
                        arrow
                        title={baseUrl
                          ? `Beide in JIRA-Suche öffnen (ein Tab, JQL: key in (${p.a['Issue key']}, ${p.b['Issue key']}))`
                          : 'JIRA-URL oben eingeben'}
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!baseUrl}
                            onClick={() => openBothInJira(p.a['Issue key'] ?? '', p.b['Issue key'] ?? '')}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip arrow title="Beide Keys in Zwischenablage kopieren">
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard?.writeText(`${p.a['Issue key']} ${p.b['Issue key']}`)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow
                    onMouseEnter={() => onRowEnter(pairKey)}
                    onMouseLeave={onRowLeave}
                  >
                    <TableCell colSpan={6} sx={{ bgcolor: 'action.hover', borderTop: 0, p: 1.5 }}>
                      <ComparisonContent a={p.a} b={p.b} shared={p.shared} score={p.score} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pairs.length > 500 && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
          Anzeige auf die ersten 500 Paare begrenzt — Schwelle erhöhen, um die Liste zu verkürzen.
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Hinweis: Score = Jaccard-Ähnlichkeit (gemeinsame Tokens ÷ vereinigte Tokens) nach
        Normalisierung (lowercase, deutsche Stopwörter raus, ≥3 Zeichen). Bei kurzen Summaries
        kann der Score hoch sein, obwohl es kein echtes Duplikat ist — die gemeinsamen Tokens in
        der Tabelle helfen schnell zu beurteilen, ob es sich lohnt zu prüfen.
      </Typography>
    </Box>
  );
}

function ScoreChip({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 85 ? 'error' : pct >= 75 ? 'warning' : 'primary';
  return <Chip size="small" color={color} label={`${pct}%`} sx={{ fontWeight: 700, minWidth: 56 }} />;
}

function IssueCell({ issue, onOpen }: { issue: import('../types/issue').Issue; onOpen: (k: string) => void }) {
  const key = issue['Issue key'] ?? '?';
  const summary = issue['Summary'] ?? '';
  const status = issue['Status'] ?? '';
  return (
    <TableCell sx={{ maxWidth: 360 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
        <Box
          component="span"
          sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline dotted' }}
          onClick={(e) => { e.stopPropagation(); onOpen(key); }}
        >
          {key}
        </Box>
        <StatusChip status={status} />
      </Stack>
      <Box sx={{ fontSize: 12, lineHeight: 1.45, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', whiteSpace: 'pre-wrap' }}>
        {summary || '—'}
      </Box>
    </TableCell>
  );
}

// Affects-Version aus dem Issue lesen — JIRA-Header variieren leicht
// ('Affects versions' oder 'Affects Version/s'). Liefert immer ein Array.
function affectsVersionsOf(issue: import('../types/issue').Issue): string[] {
  const a = issue['Affects versions'] ?? issue['Affects Version/s'];
  if (!a) return [];
  if (Array.isArray(a)) return a.filter(Boolean).map(String);
  return String(a).trim() ? [String(a)] : [];
}

function fixVersionsListOf(issue: import('../types/issue').Issue): string[] {
  const f = issue['Fix versions'];
  if (!f) return [];
  if (Array.isArray(f)) return f.filter(Boolean).map(String);
  return String(f).trim() ? [String(f)] : [];
}

// Highlight: gemeinsame Tokens (case-insensitive, word-boundary) markieren,
// damit man im Vergleich sofort sieht, wo die Übereinstimmung steckt.
function highlightTokens(text: string, tokens: string[]): React.ReactNode {
  if (!text || !tokens.length) return text || '—';
  const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).filter(Boolean);
  if (!escaped.length) return text;
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <Box
        component="mark"
        key={`${m.index}-${m[0]}`}
        sx={{
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          px: 0.4,
          borderRadius: 0.5,
          fontWeight: 700,
        }}
      >
        {m[0]}
      </Box>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Vergleichs-Block (inline unter der gehoverten Tabellenzeile):
// zwei volle Summaries NEBENEINANDER mit Highlight der gemeinsamen
// Tokens — direkter Side-by-Side-Vergleich am Bildschirm.
function ComparisonContent({ a, b, shared, score }: {
  a: import('../types/issue').Issue;
  b: import('../types/issue').Issue;
  shared: string[];
  score: number;
}) {
  const block = (label: 'A' | 'B', issue: import('../types/issue').Issue) => {
    const isDone = DONE_STATUSES.has((issue['Status'] ?? '').toLowerCase().trim());
    const fix = fixVersionsListOf(issue);
    const aff = affectsVersionsOf(issue);
    // Linkes Fenster (A, immer offen): Created + Affects Version.
    // Rechtes Fenster (B): wenn done → Fix Version + Resolved;
    //                     wenn offen → wie A (Created + Affects).
    const metaTop = (label === 'A' || !isDone)
      ? [
          { k: 'Created',  v: issue['Created']  ?? '—' },
          { k: 'Affects',  v: aff.length ? aff.join(', ') : '—' },
        ]
      : [
          { k: 'Fix',      v: fix.length ? fix.join(', ') : '—' },
          { k: 'Resolved', v: issue['Resolved'] ?? '—' },
        ];
    return (
      <Box
        sx={{
          flex: 1,
          p: 1.25,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <Chip size="small" label={label} sx={{ height: 18, fontSize: 10, fontWeight: 700 }} color={label === 'A' ? 'primary' : 'default'} />
          <Box sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>{issue['Issue key'] ?? '—'}</Box>
          <Box sx={{ flex: 1 }} />
          <StatusChip status={issue['Status'] ?? ''} />
        </Stack>
        {/* Meta-Zeile pro Block — links Created+Affects, rechts (wenn done) Fix+Resolved */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 0.75, fontSize: 10.5, color: 'text.secondary' }} flexWrap="wrap" useFlexGap>
          {metaTop.map(m => (
            <Box key={m.k}>
              <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>{m.k}:</Box>
              <Box component="span">{String(m.v)}</Box>
            </Box>
          ))}
        </Stack>
        <Box
          sx={{
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            color: 'text.primary',
          }}
        >
          {highlightTokens(issue['Summary'] ?? '', shared)}
        </Box>
      </Box>
    );
  };
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" color="warning" label={`${Math.round(score * 100)} % Match`} sx={{ fontWeight: 700 }} />
        <Box sx={{ fontSize: 11, color: 'text.secondary' }}>
          Gemeinsame Tokens (gelb markiert): <b>{shared.join(', ') || '—'}</b>
        </Box>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
        {block('A', a)}
        {block('B', b)}
      </Stack>
    </Box>
  );
}
